import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { KpiCard, Panel } from "@/lovable-demo/components/bits";
import { ClassChip, SeverityChip, SignalStatusChip } from "@/lovable-demo/components/signals";
import { Input } from "@/components/ui/input";
import { prioritise } from "@/lovable-demo/lib/signals/priority";
import { listSignals } from "@/lovable-demo/lib/signals/signals";
import {
  SIGNAL_CLASS_LABELS,
  SIGNAL_STATUS_LABELS,
  type SignalClass,
  type SignalStatus,
} from "@/lovable-demo/lib/signals/types";
import { fmtCurrency, fmtDate, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/signals")({
  head: () => ({
    meta: [
      { title: "Signal Intelligence — Broadview" },
      {
        name: "description",
        content:
          "Governed signals from warranty, production, quality, supplier, engineering, financial and compliance sources, each with a human disposition.",
      },
      { property: "og:title", content: "Signal Intelligence — Broadview" },
      {
        property: "og:description",
        content:
          "A signal is not a Problem Record: evaluate, correlate, prioritise and disposition every signal.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SignalsPage,
});

function SignalsPage() {
  const { data } = useWarranty();
  const { data: signals = [], isLoading } = useQuery({
    queryKey: ["lovable-demo", "signals"],
    queryFn: listSignals,
  });
  const [cls, setCls] = useState<"all" | SignalClass>("all");
  const [status, setStatus] = useState<"all" | SignalStatus>("all");
  const [sev, setSev] = useState<"all" | string>("all");
  const [q, setQ] = useState("");

  const ranked = useMemo(
    () =>
      signals
        .map((s) => ({ s, p: prioritise(s) }))
        .filter(({ s }) => (cls === "all" ? true : s.signalClass === cls))
        .filter(({ s }) => (status === "all" ? true : s.status === status))
        .filter(({ s }) => (sev === "all" ? true : String(s.currentSeverity ?? "none") === sev))
        .filter(({ s }) =>
          q.trim()
            ? `${s.code} ${s.title} ${s.component} ${s.supplierName}`
                .toLowerCase()
                .includes(q.trim().toLowerCase())
            : true,
        )
        .sort((a, b) => b.p.score - a.p.score),
    [signals, cls, status, sev, q],
  );

  const awaiting = signals.filter((s) => s.status === "open").length;
  const monitoring = signals.filter((s) => s.status === "monitoring").length;
  const linked = signals.filter((s) => s.status === "linked" || s.status === "converted").length;
  const codeFor = (id: string | null) => data.records.find((r) => r.id === id)?.code ?? "";

  return (
    <AppShell
      title="Signal Intelligence"
      subtitle="A signal is governed evidence of a potential issue — not a Problem Record. Every signal receives a human, auditable disposition."
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Signals"
          value={fmtNumber(signals.length)}
          hint={`${Object.keys(SIGNAL_CLASS_LABELS).length} signal classes`}
        />
        <KpiCard
          label="Awaiting disposition"
          value={fmtNumber(awaiting)}
          hint="Needs a human decision"
        />
        <KpiCard label="Monitoring" value={fmtNumber(monitoring)} hint="No Problem Record opened" />
        <KpiCard
          label="Linked / converted"
          value={fmtNumber(linked)}
          hint="Attached to a governed record"
        />
      </div>

      <Panel
        className="mt-4"
        title="Signal priority"
        subtitle="Advisory ordering across severity, trend, exposure, cost, recurrence, safety, customer impact and data confidence. Separate from the Problem Record Top 5."
      >
        <div className="mb-3 flex flex-wrap gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search signals"
            className="h-8 w-56"
          />
          <Select
            value={cls}
            onChange={(v) => setCls(v as never)}
            label="Class"
            options={[["all", "All classes"], ...Object.entries(SIGNAL_CLASS_LABELS)]}
          />
          <Select
            value={status}
            onChange={(v) => setStatus(v as never)}
            label="Status"
            options={[["all", "All statuses"], ...Object.entries(SIGNAL_STATUS_LABELS)]}
          />
          <Select
            value={sev}
            onChange={setSev}
            label="Severity"
            options={[
              ["all", "All severities"],
              ["5", "S5 Critical"],
              ["4", "S4 High"],
              ["3", "S3 Moderate"],
              ["2", "S2 Low"],
              ["1", "S1 Minor"],
              ["none", "Not assessed"],
            ]}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-xs">
            <thead className="text-[0.66rem] uppercase tracking-[0.14em] text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3">Priority</th>
                <th className="py-2 pr-3">Signal</th>
                <th className="py-2 pr-3">Class</th>
                <th className="py-2 pr-3">Magnitude</th>
                <th className="py-2 pr-3">Exposure</th>
                <th className="py-2 pr-3">Severity</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Detected</th>
                <th className="py-2">Record</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map(({ s, p }) => (
                <tr key={s.id} className="border-b border-border/50 align-top">
                  <td className="num py-2 pr-3 text-base font-semibold text-primary">{p.score}</td>
                  <td className="py-2 pr-3">
                    <Link
                      to="/demo/signal/$id"
                      params={{ id: s.id }}
                      className="font-semibold text-foreground hover:text-primary"
                    >
                      <span className="num">{s.code}</span> — {s.title}
                    </Link>
                    <p className="mt-0.5 text-muted-foreground">
                      {s.component}
                      {s.supplierName ? ` · ${s.supplierName}` : ""}
                    </p>
                  </td>
                  <td className="py-2 pr-3">
                    <ClassChip cls={s.signalClass} />
                  </td>
                  <td className="num py-2 pr-3">
                    {fmtNumber(s.magnitude, s.magnitude < 10 ? 2 : 0)}{" "}
                    <span className="text-muted-foreground">{s.magnitudeUnit}</span>
                    <p className="text-muted-foreground capitalize">{s.trendDirection}</p>
                  </td>
                  <td className="num py-2 pr-3">
                    {fmtCurrency(s.financialExposure)}
                    <p className="text-muted-foreground">{fmtNumber(s.exposureUnits)} units</p>
                  </td>
                  <td className="py-2 pr-3">
                    <SeverityChip severity={s.currentSeverity} />
                  </td>
                  <td className="py-2 pr-3">
                    <SignalStatusChip status={s.status} />
                  </td>
                  <td className="num py-2 pr-3">{fmtDate(s.detectedAt)}</td>
                  <td className="py-2">
                    {s.linkedRecordId ? (
                      <Link
                        to="/demo/problem-record/$id"
                        params={{ id: s.linkedRecordId }}
                        className="num text-primary hover:underline"
                      >
                        {codeFor(s.linkedRecordId)}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {!isLoading && ranked.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-6 text-center text-muted-foreground">
                    No signals match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </AppShell>
  );
}

function Select({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
  label: string;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 rounded border border-border bg-elevated px-2 text-xs"
    >
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}
